import React, { useEffect, useRef } from "react";
import { searchSkuData } from "../../../data/mockSkuData";
import { toast } from "../../../components/AppToast";
import { fetchDataAndPatchFields } from "../../../services/item/item";
import PartnerAutoSearch from "../../../components/PartnerAutoSearch";
import PartnerLookupField from "../../../components/PartnerLookupField";
import LookupField from "../../../components/LookupField";
import { SUPPLIER_LOOKUP, CUSTOMER_LOOKUP } from "../../../components/lookupConfigs";

export default function GenericFormTabPo({
  fields,
  formData,
  onChange,
  readOnly = false,
  formSubmissionAttempted = false,
  isEditMode = false,
  formDataPo,
  /** Optional: map Item Master lookup (`item/getSkuDetailsById`) result to form fields (e.g. Gate Pass uses sku_code / item_name). */
  mergeSkuLookupResult = null,
  /** Optional: override default `item/getSkuDetailsById` lookup (e.g. STO `item-by-sku`). */
  fetchSkuDetails = null,
  /** Optional: `{ [fieldName]: ReactNode }` rendered as an input-group addon after that field's input. */
  fieldAddons = null,
}) {
  const poNumberFetchedRef = useRef(false);

  // console.log("PO changed:", formDataPo);

  useEffect(() => {

    // console.log("GenericFormTabPo - formData changed:", formData);
    const prefetchValues = async () => {
      if (poNumberFetchedRef.current) return;

      const updatedValues = {};
      let hasUpdates = false;

      // Fix: safely access formData.raw and formData.raw.purchase_order
      for (const field of fields) {
        // Ensure Status default "New"
        if (
          field.name === "status" &&
          (formData.status === undefined ||
            formData.status === null ||
            formData.status === "")
        ) {
          // Patch: check existence before accessing purchase_order
          const statusValue = formData?.raw?.purchase_order?.status ??
            "New";
          updatedValues.status = statusValue;
          hasUpdates = true;
        }
      }

      if (hasUpdates) {
        Object.entries(updatedValues).forEach(([name, value]) => {
          onChange({ target: { name, value } });
        });
      }
    };

    prefetchValues();
  }, [fields, formData, onChange]);

  const handleSearch = (fieldName) => {
    if (fieldName === "SKUID" && formData.SKUID) {
      const skuId = formData.SKUID;
      const results = searchSkuData(skuId);

      if (results.length === 0) {
        toast.success(`No results found for SKU ID: ${skuId}`);
      }
    }
  };

  const effectiveEditMode = isEditMode || Boolean(formData?.__isEditMode);

  const patch = (map) =>
    Object.entries(map).forEach(([name, value]) => onChange({ target: { name, value } }));

  /** Supplier picked from `supplier/fetch-multi-dynamic` → Source/Supplier address section. */
  const applySupplier = (fieldName, rec) => {
    const s = SUPPLIER_LOOKUP.normalize(rec);
    patch({
      [fieldName]: s.name,
      supplierId: s.id,
      organizationCustomerId: s.organizationCustomerId,
      addressLine1: s.address1,
      addressLine2: s.address2,
      addressLine3: "",
      addressLine4: "",
      city: s.city,
      state: s.state,
      country: s.country,
      sellersReference: s.id,
      contact: s.mobile,
      email: s.email,
    });
  };

  /** Customer picked from `customer-group/fetch-multi-dynamic` → Destination/Buyer address section. */
  const applyCustomer = (fieldName, rec) => {
    const c = CUSTOMER_LOOKUP.normalize(rec);
    patch({
      [fieldName]: c.name,
      buyerAddressLine1: c.address1,
      buyerAddressLine2: c.address2,
      buyerAddressLine3: "",
      buyerAddressLine4: "",
      buyerCity: c.city,
      buyerState: c.state,
      buyerCountry: c.country,
      buyersReference: c.id,
      buyerContact: c.mobile,
      buyerEmail: c.email,
    });
  };

  return (
    <div className="row gx-2 mx-2">
      {fields.map((field) => {
        if (field.hiddenOnCreate && !readOnly) {
          return null;
        }

        const fieldReadOnly =
          field.readOnly ||
          readOnly ||
          (effectiveEditMode && (field.readOnlyInEdit || field.name === "buyerOwner"));
        const isRequired = field.required && !fieldReadOnly;
        const fieldValue = formData[field.name];
        const isEmpty = fieldValue === null || fieldValue === undefined || fieldValue === '' ||
          (typeof fieldValue === 'string' && fieldValue.trim() === '');

        // Only show error styling if form submission was attempted AND field is required and empty
        const hasError = formSubmissionAttempted && isRequired && isEmpty;

        return (
          <div
            className={`col-md-${field.type === "checkbox" ? "12" : "2"}`}
            key={field.name}
          >
            <div className="form-group mb-2">
              {field.type === "checkbox" ? (
                <div className="form-check">
                  <input
                    id={field.name}
                    type="checkbox"
                    className={`form-check-input ${hasError ? 'is-invalid' : ''}`}
                    name={field.name}
                    checked={!!formData[field.name]}
                    onChange={(e) =>
                      onChange({
                        target: {
                          name: field.name,
                          value: e.target.checked,
                        },
                      })
                    }
                    required={field.required}
                    disabled={fieldReadOnly}
                  />
                  <label htmlFor={field.name} className="form-check-label">
                    {field.label}{" "}
                    {/* Always show required asterisk for required fields */}
                    {isRequired && <span className="text-danger">*</span>}
                  </label>
                  {hasError && (
                    <div className="invalid-feedback">
                      {field.label} is required
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <label htmlFor={field.name} className="form-label">
                    {field.label}{" "}
                    {/* Always show required asterisk for required fields */}
                    {isRequired && <span className="text-danger">*</span>}
                  </label>

                  {field.type === "textarea" ? (
                    <textarea
                      id={field.name}
                      className={`form-control ${hasError ? "is-invalid" : ""}`}
                      name={field.name}
                      rows={field.rows ?? 2}
                      value={formData[field.name] ?? ""}
                      onChange={onChange}
                      required={field.required}
                      disabled={fieldReadOnly}
                      placeholder={field.placeholder}
                    />
                  ) : field.type === "select" ? (
                    <select
                      id={field.name}
                      className={`form-select ${hasError ? 'is-invalid' : ''}`}
                      name={field.name}
                      value={formData[field.name] ?? ""}
                      onChange={onChange}
                      required={field.required}
                      disabled={fieldReadOnly}
                    >
                      <option value="">Select</option>
                      {field.options?.map((opt) => (
                        <option
                          key={typeof opt === "object" ? opt.value : opt}
                          value={typeof opt === "object" ? opt.value : opt}
                        >
                          {typeof opt === "object" ? opt.label : opt}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "multiselect" ? (
                    <select
                      id={field.name}
                      className={`form-select ${hasError ? 'is-invalid' : ''}`}
                      multiple
                      name={field.name}
                      value={formData[field.name] || []}
                      onChange={(e) =>
                        onChange({
                          target: {
                            name: field.name,
                            value: Array.from(
                              e.target.selectedOptions,
                              (opt) => opt.value
                            ),
                          },
                        })
                      }
                      disabled={fieldReadOnly}
                    >
                      {field.options?.map((opt) => (
                        <option
                          key={typeof opt === "object" ? opt.value : opt}
                          value={typeof opt === "object" ? opt.value : opt}
                        >
                          {typeof opt === "object" ? opt.label : opt}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "partnerLookup" ? (
                    <PartnerLookupField
                      name={field.name}
                      value={formData[field.name] ?? ""}
                      disabled={fieldReadOnly}
                      required={isRequired}
                      error={hasError ? `${field.label} is required` : ""}
                      modalTitle={`Select ${field.label}`}
                      onSelect={(partner) =>
                        onChange({
                          target: { name: field.name, value: partner?.partner_id || "" },
                        })
                      }
                    />
                  ) : field.type === "supplierLookup" || field.type === "customerLookup" ? (
                    <LookupField
                      name={field.name}
                      value={formData[field.name] ?? ""}
                      config={field.type === "supplierLookup" ? SUPPLIER_LOOKUP : CUSTOMER_LOOKUP}
                      editable
                      disabled={fieldReadOnly}
                      required={isRequired}
                      error={hasError ? `${field.label} is required` : ""}
                      onChange={onChange}
                      onSelect={(rec) =>
                        field.type === "supplierLookup"
                          ? applySupplier(field.name, rec)
                          : applyCustomer(field.name, rec)
                      }
                    />
                  ) : field.type === "autosearch" ? (

                       <PartnerAutoSearch
                      value={formData[field.name] ?? ""}
                      required={isRequired}
                      error={hasError ? `${field.label} is required` : ""}
                      onSelect={(partner) => {
                        if (!partner) return;

                        onChange({
                          target: {
                            name: field.name,
                            value: partner?.partner_id || "",
                          },
                        });

                        const addr = partner?.address || {};
                        const isBuyer = field.name === "buyerOwner";
                        const isSupplier = field.name === "supplier";

                        if (isBuyer) {
                          const buyerMap = {
                            buyerAddressLine1: addr.partner_address1 || "",
                            buyerAddressLine2: addr.partner_address2 || "",
                            buyerAddressLine3: "",
                            buyerAddressLine4: "",
                            buyerCity: addr.partner_city || "",
                            buyerState: addr.partner_state || "",
                            buyerCountry: addr.partner_country || "",
                            buyerReference: partner.partner_id || "",
                            buyerContact: partner.partner_mobile || "",
                            buyerEmail: partner.partner_email || "",
                          };
                          Object.entries(buyerMap).forEach(([key, value]) =>
                            onChange({ target: { name: key, value } })
                          );
                        }

                        if (isSupplier) {
                          const organizationCustomerId =
                            partner?.organizationCustomerId?._id ??
                            partner?.organizationCustomerId ??
                            partner?._id ??
                            partner?.partner_id ??
                            "";
                          onChange({
                            target: { name: "organizationCustomerId", value: organizationCustomerId },
                          });

                          const supplierMap = {
                            addressLine1: addr.partner_address1 || "",
                            addressLine2: addr.partner_address2 || "",
                            addressLine3: "",
                            addressLine4: "",
                            city: addr.partner_city || "",
                            state: addr.partner_state || "",
                            country: addr.partner_country || "",
                            supplierReference: partner.partner_id || "",
                            contact: partner.partner_mobile || "",
                            email: partner.partner_email || "",
                          };
                          Object.entries(supplierMap).forEach(([key, value]) =>
                            onChange({ target: { name: key, value } })
                          );
                        }
                      }}
                    />



                  )

                    : field.searchable ? (
                      <div className="input-group">
                        <input
                          id={field.name}
                          type={field.type}
                          className={`form-control ${hasError ? 'is-invalid' : ''}`}
                          name={field.name}
                          value={formData[field.name] ?? ""}
                          onChange={onChange}
                          required={field.required}
                          disabled={fieldReadOnly}
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => handleSearch(field.name)}
                          disabled={readOnly}
                        >
                          <i className="bi bi-search"></i>
                        </button>
                      </div>
                    ) : field.hasSearch ? (
                      <input
                        id={field.name}
                        type={field.type}
                        className={`form-control ${hasError ? 'is-invalid' : ''}`}
                        name={field.name}
                        value={formData[field.name] ?? ""}
                        onChange={onChange}
                        onKeyDown={async (e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();

                            const result =
                              typeof fetchSkuDetails === "function"
                                ? await fetchSkuDetails(field.name, formData[field.name])
                                : await fetchDataAndPatchFields(
                                    field.name,
                                    formData[field.name],
                                    formDataPo?.organizationCustomerId
                                  );
                            if (result) {
                              if (typeof mergeSkuLookupResult === "function") {
                                mergeSkuLookupResult(result, onChange, field);
                              } else {
                                Object.entries(result).forEach(([key, value]) => {
                                  onChange({ target: { name: key, value } });
                                });
                              }
                            }
                          }
                        }}
                        required={field.required}
                        disabled={fieldReadOnly}
                      />
                    ) : fieldAddons?.[field.name] ? (
                      <div className="input-group">
                        <input
                          id={field.name}
                          type={field.type}
                          className={`form-control ${hasError ? 'is-invalid' : ''}`}
                          name={field.name}
                          value={formData[field.name] ?? ""}
                          onChange={onChange}
                          required={field.required}
                          disabled={fieldReadOnly}
                        />
                        {fieldAddons[field.name]}
                      </div>
                    ) : (
                      <input
                        id={field.name}
                        type={field.type}
                        className={`form-control ${hasError ? 'is-invalid' : ''}`}
                        name={field.name}
                        value={formData[field.name] ?? ""}
                        onChange={onChange}
                        required={field.required}
                        disabled={fieldReadOnly}
                      />
                    )}


                  {hasError && (
                    <div className="invalid-feedback">
                      {field.label} is required
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


